import { Injectable } from '@angular/core';
import { UserHelperService } from '@helpers/user-helper.service';
import { UserPreferencesI } from '@interfaces/user-preferences.interface';
import { merge } from 'lodash';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { UsersService } from './users.service';
import { Language } from '@interfaces/language.interface';
import { LanguageHelper } from '@helpers/language-helper.service';

@Injectable({
  providedIn: 'root',
})
export class UserPreferencesService {
  private userPreferencesSubject = new BehaviorSubject<UserPreferencesI | undefined>(undefined);
  public userPreferences$ = this.userPreferencesSubject.asObservable();

  private getCurrentUserId(): string | undefined {
    const userAttributes = this.userHelperService.getUserAttributes();
    return userAttributes?.['custom:userId'];
  }

  constructor(
    private userHelperService: UserHelperService,
    private userService: UsersService,
    private readonly languageHelper: LanguageHelper
  ) {
    this.init();
  }

  public init() {
    if (this.getCurrentUserId()) {
      this.loadPreferencesFromLocalStorage();
    }
  }

  async getPreferences(): Promise<UserPreferencesI | undefined> {
    return firstValueFrom(this.userPreferences$);
  }

  getPreferencesObservable(): Observable<UserPreferencesI | undefined> {
    return this.userPreferences$;
  }

  async patchPreferences(
    preferences: Partial<UserPreferencesI>,
    patchRemote: boolean = true
  ): Promise<(UserPreferencesI & Partial<UserPreferencesI>) | undefined> {
    const currentPreferences = await this.getPreferences();
    const currentUserId = this.getCurrentUserId();
    if (currentPreferences && currentUserId) {
      const newPreferences = merge(currentPreferences, preferences);
      this.userPreferencesSubject.next(newPreferences);
      this.savePreferencesOnLocalStorage(newPreferences);
      if (patchRemote) {
        await firstValueFrom(this.userService.updateUserPreferences(currentUserId, newPreferences));
      }
      return newPreferences;
    }
    return undefined;
  }

  setupPreferences(preferences: UserPreferencesI) {
    this.userPreferencesSubject.next(preferences);
    this.savePreferencesOnLocalStorage(preferences);
  }

  private async loadPreferencesFromLocalStorage() {
    const currentPreferences = localStorage.getItem('userPreferences');
    if (currentPreferences) {
      try {
        this.userPreferencesSubject.next(JSON.parse(currentPreferences) as UserPreferencesI);
      } catch (error) {
        throw new Error(`Error parsing user preferences: ${error}`);
      }
    } else {
      const user = await firstValueFrom(this.userService.getUserById(this.getCurrentUserId()));

      if (user) {
        this.setupPreferences(user.preferences);
      }
    }
  }

  private savePreferencesOnLocalStorage(preferences: UserPreferencesI) {
    localStorage.setItem('userPreferences', JSON.stringify(preferences));
  }

  public async getLanguage(): Promise<Language | undefined> {
    const preferences = await this.getPreferences();

    if (preferences && preferences.language) {
      return this.languageHelper.findLanguageById(preferences.language);
    }

    return this.languageHelper.getDefaultLanguage();
  }
}
